import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, Image as ImageIcon, Megaphone, Pencil } from "lucide-react";

/**
 * Subtle red "ⓘ" button shown to the right of the establishment name on
 * the merchant dashboard. Clicking it reveals a compact tooltip card
 * documenting the four shortcut actions (Icône / Logo / Message / Nom).
 *
 * Implementation notes:
 *  - The bubble is rendered through a React Portal at document.body so it
 *    can escape the `overflow-hidden` parent card. Positioning is
 *    computed from `getBoundingClientRect()` on the trigger button,
 *    using `position: fixed`.
 *  - Closes on outside-click, on page scroll, and on Escape.
 *  - Visual language follows the "LÉGENDES DES ACTIONS" tooltip used
 *    elsewhere: overline title, compact rows with bold label + colon +
 *    short description. Each row shows the *exact* same Lucide icon
 *    (blue) as the corresponding shortcut button on the dashboard.
 */

const RED = "#E5484D";

const ITEMS = [
  { Icon: Sparkles,   label: "Icône",   desc: "Choisir une icône représentant votre établissement." },
  { Icon: ImageIcon,  label: "Logo",    desc: "Ajouter le logo de votre entreprise ou établissement." },
  { Icon: Megaphone,  label: "Message", desc: "Message de bienvenue affiché sur l'écran public." },
  { Icon: Pencil,     label: "Nom",     desc: "Modifier le nom de votre établissement." },
];

export default function EstablishmentInfoTooltip() {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null); // { top, right }
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // Position the bubble relative to the trigger (fixed coordinates so it
  // escapes any overflow-hidden ancestor).
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setCoords({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inBtn && !inPanel) setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
      window.removeEventListener("scroll", handleScroll, { capture: true });
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Informations sur les fonctions de l'établissement"
        aria-expanded={open}
        title="Informations établissement"
        className="group relative inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-full bg-white border transition-all duration-200 ease-out shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(229,72,77,0.18)] hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        style={{ borderColor: RED, color: RED }}
        data-testid="merchant-info-tooltip-btn"
      >
        <span
          aria-hidden="true"
          className="font-serif italic font-semibold leading-none select-none transition-transform duration-200 group-hover:scale-110"
          style={{ fontSize: 14, color: RED }}
        >
          i
        </span>
      </button>

      {open && coords && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Fonctions de l'établissement"
          className="fixed z-[1000] w-[min(18rem,calc(100vw-1rem))] rounded-xl border border-border bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)] p-3 animate-in fade-in zoom-in-95 duration-200 origin-top-right"
          style={{ top: coords.top, right: coords.right }}
          data-testid="merchant-info-tooltip-panel"
        >
          <span
            aria-hidden="true"
            className="absolute -top-1.5 right-3 w-3 h-3 bg-white border-t border-l border-border rotate-45"
          />
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-subtle mb-2 px-0.5">
            Légendes des fonctions
          </p>
          <ul className="space-y-1.5">
            {ITEMS.map(({ Icon, label, desc }) => (
              <li
                key={label}
                className="flex items-start gap-2 text-[12px] leading-snug text-ink"
                data-testid={`merchant-info-tooltip-item-${label.toLowerCase()}`}
              >
                <Icon size={14} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="font-bold">{label} :</span>{" "}
                  <span className="text-subtle">{desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
}
